import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  ChartBar, Article, Image, Target, Queue,
  ArrowClockwise, Sparkle, TrendUp,
  Eye, InstagramLogo, FacebookLogo, LinkedinLogo, PinterestLogo,
  TiktokLogo, Star, Lightning, ShareNetwork, CursorClick, Users, CalendarBlank
} from '@phosphor-icons/react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from 'recharts';
import ContextHelpButton from './ContextHelpButton';

const COLORS = {
  reel: '#ec4899', carousel: '#6366f1', prompted_reel: '#a855f7',
  awareness: '#3b82f6', education: '#22c55e', monetizing: '#f97316', unknown: '#6b7280',
  draft: '#a855f7', scheduled: '#f97316', published: '#22c55e', failed: '#ef4444',
  queued: '#3b82f6',
  facebook: '#1877F2', instagram: '#E4405F', linkedin: '#0A66C2',
  pinterest: '#E60023', tiktok: '#ffffff',
};

const PLATFORM_ICONS = {
  facebook: FacebookLogo, instagram: InstagramLogo, linkedin: LinkedinLogo,
  pinterest: PinterestLogo, tiktok: TiktokLogo,
};

const METRIC_ICONS = {
  views: Eye,
  engagement: TrendUp,
  sharing: ShareNetwork,
  clicks: CursorClick,
  follows: Users,
};

const TIME_RANGES = [7, 30, 90, 183];
const LOCALE_BY_LANGUAGE = {
  it: 'it-IT',
  en: 'en-GB',
  es: 'es-ES',
  fr: 'fr-FR',
};

function resolvedLocale(language) {
  const normalized = String(language || 'it').toLowerCase();
  return LOCALE_BY_LANGUAGE[normalized.split('-')[0]] || language || 'it-IT';
}

function formatChartDate(value, locale, options = {}) {
  if (!value) return '';
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    ...(options.year ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

function buildDateAxis(rangeDays, endValue) {
  const end = endValue ? new Date(`${endValue}T00:00:00Z`) : new Date();
  end.setUTCHours(0, 0, 0, 0);
  return Array.from({ length: rangeDays }, (_, index) => {
    const day = new Date(end);
    day.setUTCDate(end.getUTCDate() - (rangeDays - 1) + index);
    return day.toISOString().slice(0, 10);
  });
}

function buildChartTicks(dateAxis, rangeDays) {
  if (!dateAxis.length) return [];
  const step = rangeDays <= 7 ? 1 : rangeDays <= 30 ? 5 : rangeDays <= 90 ? 15 : 30;
  const ticks = dateAxis.filter((_, index) => index % step === 0);
  const last = dateAxis[dateAxis.length - 1];
  if (ticks[ticks.length - 1] !== last) ticks.push(last);
  return ticks;
}

// ANALYTICS-MULTI-SOCIAL-001A: builds the compact per-platform sync
// summary lines from the new backward-compatible `platform_results`
// field. Falls back to null (caller then shows the legacy single-line
// message) when talking to an older backend that doesn't send it yet.
function buildSyncSummaryLines(res, t, platformsById) {
  const results = res?.platform_results;
  if (!results || typeof results !== 'object' || !Object.keys(results).length) return null;
  return Object.entries(results).map(([platformId, result]) => {
    const label = platformsById[platformId] || platformId;
    // "count" favors synced_posts (post-level sync), falling back to
    // synced_account_days for platforms that only ever get an account
    // snapshot (e.g. Pinterest) — either way it's the number the
    // localized status message below interpolates.
    const count = result.synced_posts || result.synced_account_days || 0;
    const message = t(`analytics.syncSummary.status.${result.status}`, { count, defaultValue: t('analytics.syncSummary.status.error') });
    return t('analytics.syncSummary.line', { platform: label, message });
  });
}

// ANALYTICS-PRODUCTION-CAPABILITIES-001B: never render a raw provider
// error — the backend only ever sends a stable, sanitized `reason_code`
// for a known structural capability limitation (e.g. a Facebook Page
// mismatch, a LinkedIn token missing the read scope). Localize purely
// from that identifier; unsupported/no_metrics keep their existing
// backward-compatible messages.
function platformAvailabilityMessage(item, platform, t) {
  if (item.reason_code) {
    return t(`analytics.availability.reasonCode.${item.reason_code}`, {
      platform,
      defaultValue: t('analytics.availability.noMetrics'),
    });
  }
  if (item.status === 'unsupported') return t('analytics.availability.unsupported', { platform });
  return t('analytics.availability.noMetrics');
}

function normalizeDashboardRange(dashboard, rangeDays) {
  if (!dashboard) return dashboard;
  const dateAxis = buildDateAxis(rangeDays, dashboard.range_end);
  const platformIds = (dashboard.platforms || []).map((platform) => platform.id);
  const normalizedSeries = {};

  Object.entries(dashboard.series || {}).forEach(([metric, rows]) => {
    const rowsByDate = new Map((rows || []).map((row) => [row.date, row]));
    normalizedSeries[metric] = dateAxis.map((date) => {
      const row = { date, ...(rowsByDate.get(date) || {}) };
      platformIds.forEach((platform) => {
        if (row[platform] == null) row[platform] = 0;
      });
      return row;
    });
  });

  return {
    ...dashboard,
    series: normalizedSeries,
    range_days: rangeDays,
    range_start: dateAxis[0],
    range_end: dateAxis[dateAxis.length - 1],
  };
}

function StatBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="capitalize">{label}</span>
        <span className="text-[var(--text-muted)]">{value} ({Math.round(pct)}%)</span>
      </div>
      <div className="w-full h-2.5 rounded-full bg-[var(--bg-secondary)] overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }}
          className="h-full rounded-full" style={{ background: color }} />
      </div>
    </div>
  );
}

function MetricChip({ label, value, icon: Icon, color, locale }) {
  return (
    <div className="flex flex-col items-center p-2.5 md:p-3 rounded-xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
      <Icon size={18} weight="fill" style={{ color, marginBottom: 4 }} />
      <p className="text-base md:text-lg font-bold">{typeof value === 'number' ? value.toLocaleString(locale) : value}</p>
      <p className="text-[10px] text-[var(--text-muted)] uppercase font-semibold text-center">{label}</p>
    </div>
  );
}

function formatNumber(value, locale = 'it-IT') {
  if (typeof value !== 'number') return value || 0;
  return value.toLocaleString(locale);
}

function truncateText(value, max = 78) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

function formatPostMetricValue(post, metricKey, locale) {
  const normalized = post?.normalized || {};
  return formatNumber(normalized?.[metricKey] || 0, locale);
}

function PostAwareDot({ cx, cy, payload, stroke, platformId, rangeDays }) {
  if (cx == null || cy == null) return null;
  const hasPosts = Boolean(payload?.__posts?.[platformId]?.length);
  if (!hasPosts && rangeDays > 30) return null;
  const radius = hasPosts ? 4.5 : 2.5;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={stroke}
      stroke={hasPosts ? '#ffffff' : stroke}
      strokeWidth={hasPosts ? 1.5 : 0}
      opacity={hasPosts ? 1 : 0.8}
    />
  );
}

function ChartTooltip({ active, payload, label, locale, metricKey, platformsById, t }) {
  if (!active || !payload?.length) return null;
  const postsByPlatform = payload[0]?.payload?.__posts || {};
  return (
    <div className="rounded-xl p-3 text-xs shadow-xl" style={{ background: 'rgba(10,15,28,0.96)', border: '1px solid var(--border-color)' }}>
      <p className="font-semibold mb-2">{formatChartDate(label, locale, { year: true })}</p>
      <div className="space-y-1">
        {payload.map((item) => (
          <div key={item.dataKey} className="flex items-center justify-between gap-6">
            <span style={{ color: item.color }}>{item.name}</span>
            <strong>{formatNumber(item.value, locale)}</strong>
          </div>
        ))}
      </div>
      {Object.keys(postsByPlatform).length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{t('analytics.publishedPosts')}</p>
          {Object.entries(postsByPlatform).map(([platformId, posts]) => (
            <div key={platformId} className="space-y-1.5">
              <p className="font-semibold" style={{ color: COLORS[platformId] || '#fff' }}>
                {platformsById[platformId] || platformId}
              </p>
              {posts.map((post) => (
                <div key={`${platformId}-${post.post_id || post.content_id}`} className="rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2">
                  <p className="text-[11px] leading-relaxed text-white">{truncateText(post.hook_text, 88) || t('analytics.untitledPost')}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-[var(--text-muted)]">
                    <span>{post.format || '—'}</span>
                    <span>{t('analytics.metricValue', { value: formatPostMetricValue(post, metricKey, locale) })}</span>
                    {post.measured ? <span>{t('analytics.measured')}</span> : <span>{t('analytics.notMeasured')}</span>}
                    {post.post_url && (
                      <a
                        href={post.post_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-cyan-200 underline underline-offset-2"
                      >
                        {t('analytics.openPost')}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Analytics({ project, compact }) {
  const { api } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = resolvedLocale(i18n.resolvedLanguage || i18n.language);
  const [data, setData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [postMetrics, setPostMetrics] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [activeMetric, setActiveMetric] = useState('engagement');
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [syncSummaryLines, setSyncSummaryLines] = useState(null);
  const [rangeDays, setRangeDays] = useState(30);
  const loadRequestRef = useRef(0);
  // ANALYTICS-MULTI-SOCIAL-001A: `activePlatforms` used to be seeded ONCE
  // from the first dashboard response (`current.length ? current : ...`)
  // — once non-empty, any platform discovered in a LATER response (e.g.
  // after Sync surfaces Facebook/LinkedIn/Pinterest for the first time)
  // was silently never added, even though the dashboard itself already
  // reported it. `knownPlatformIds` tracks every platform id ever seen so
  // a genuinely new one can be told apart from one merely still present;
  // `disabledPlatformIds` tracks ids the user explicitly turned off via
  // `togglePlatform`, so reconciliation never re-activates a deliberate
  // choice — only a platform this component has truly never seen before.
  const knownPlatformIdsRef = useRef(new Set());
  const disabledPlatformIdsRef = useRef(new Set());

  const reconcileActivePlatforms = useCallback((latestPlatforms) => {
    const latestIds = (latestPlatforms || []).map((p) => p.id);
    const latestSet = new Set(latestIds);
    const known = knownPlatformIdsRef.current;
    const isFirstLoad = known.size === 0;
    // Precomputed BEFORE `known` is mutated below — `setActivePlatforms`'s
    // updater callback can run asynchronously (e.g. after a promise
    // resolution), by which point the `known.add(...)` calls further down
    // would already have executed, making every id look "already known"
    // if re-checked live inside the updater. Capturing the newly-
    // discovered set now, while `known` still reflects the PREVIOUS
    // dashboard response, is what makes auto-activation of a genuinely
    // new platform actually work.
    const newlyDiscoveredIds = new Set(latestIds.filter((id) => !known.has(id)));
    const disabledIds = disabledPlatformIdsRef.current;

    setActivePlatforms((current) => {
      const nextSet = new Set(current);
      latestIds.forEach((id) => {
        if (isFirstLoad || (newlyDiscoveredIds.has(id) && !disabledIds.has(id))) {
          // First-ever load activates every returned platform; after
          // that, only a genuinely new id (never known before) is
          // auto-activated, and never one the user already disabled.
          nextSet.add(id);
        }
      });
      // A platform id the dashboard no longer reports (e.g. disconnected)
      // must never linger in the active set.
      return [...nextSet].filter((id) => latestSet.has(id));
    });

    latestIds.forEach((id) => known.add(id));
  }, []);

  const load = useCallback(() => {
    if (!project?.id) return;
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    const rangeParams = { params: { range_days: rangeDays } };
    Promise.all([
      api.get(`/analytics/${project.id}`),
      api.get(`/analytics/${project.id}/post-metrics`, rangeParams),
      api.get(`/analytics/${project.id}/dashboard`, rangeParams),
    ]).then(([r1, r2, r3]) => {
      if (requestId !== loadRequestRef.current) return;
      const dashboardData = r3.data || {};
      setData(r1.data);
      setPostMetrics(r2.data || []);
      setDashboard(normalizeDashboardRange(dashboardData, rangeDays));
      reconcileActivePlatforms(dashboardData.platforms);
    }).catch(() => {}).finally(() => {
      if (requestId === loadRequestRef.current) setLoading(false);
    });
  }, [api, project?.id, rangeDays, reconcileActivePlatforms]);

  useEffect(() => { load(); }, [load]);

  const syncMetrics = async () => {
    setSyncing(true); setSyncMsg(''); setSyncSummaryLines(null);
    try {
      const { data: res } = await api.post(`/analytics/${project.id}/sync`);
      const [r2, r3] = await Promise.all([
        api.get(`/analytics/${project.id}/post-metrics`, { params: { range_days: rangeDays } }),
        api.get(`/analytics/${project.id}/dashboard`, { params: { range_days: rangeDays } }),
      ]);
      setPostMetrics(r2.data || []);
      const dashboardData = r3.data || null;
      setDashboard(normalizeDashboardRange(dashboardData, rangeDays));
      reconcileActivePlatforms(dashboardData?.platforms);
      const platformsById = Object.fromEntries((dashboardData?.platforms || []).map((p) => [p.id, p.label]));
      const summaryLines = buildSyncSummaryLines(res, t, platformsById);
      if (summaryLines) {
        setSyncSummaryLines(summaryLines);
      } else {
        // Backward compatibility: an older backend without `platform_results`.
        setSyncMsg(t('analytics.syncSuccess', {
          count: res.synced,
          notes: res.errors?.length ? t('analytics.syncNotes', { count: res.errors.length }) : '',
        }));
      }
    } catch (e) {
      setSyncMsg(t('analytics.syncError'));
    } finally { setSyncing(false); }
  };

  const loadAiInsights = async () => {
    setLoadingAi(true);
    try {
      const { data: ins } = await api.get(`/analytics/${project.id}/ai-insights`, { params: { range_days: rangeDays } });
      setAiInsights(ins);
    } catch { } finally { setLoadingAi(false); }
  };

  const campaignQueueStatuses = data?.queue_by_status || {};
  const campaignQueuedCount = Object.values(campaignQueueStatuses)
    .reduce((sum, count) => sum + (Number(count) || 0), 0);
  const campaignReportReady = campaignQueuedCount > 0 &&
    Number(campaignQueueStatuses.published || 0) === campaignQueuedCount;

  const downloadCampaignReport = async () => {
    setLoadingReport(true);
    setReportError('');
    try {
      const { data: report } = await api.get(`/analytics/${project.id}/campaign-report`);
      const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[ch]));
      const lines = (report.analytics_review?.recommendations || []).map((x) => `<li>${esc(x)}</li>`).join('');
      const values = Object.entries(report.totals || {}).map(([k, v]) =>
        `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');
      const platforms = Object.entries(report.platforms || {}).map(([k, v]) =>
        `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');
      const html = `<!doctype html><html lang="${esc(locale)}"><head><meta charset="utf-8">
<title>${esc(report.campaign_name)} — ${esc(t('analytics.campaignReport.title'))}</title>
<style>body{font:16px/1.55 system-ui,Arial,sans-serif;max-width:850px;margin:56px auto;color:#161616;padding:0 28px}
h1{font-size:32px}h2{margin-top:36px;border-bottom:1px solid #ccc;padding-bottom:8px}th{text-align:left}
table{border-collapse:collapse;width:100%}th,td{padding:8px;border-bottom:1px solid #ddd}
small{color:#555}li{margin:7px 0}@media print{body{margin:10mm auto}}</style></head><body>
<h1>${esc(report.campaign_name)}</h1>
<p>${esc(t('analytics.campaignReport.title'))} · ${esc(report.generated_at?.slice(0,10))}</p>
<p>${esc(report.first_publication || '—')} — ${esc(report.last_publication || '—')}</p>
<h2>${esc(t('analytics.campaignReport.publishing'))}</h2>
<p>${esc(t('analytics.campaignReport.posts'))}: ${esc(report.published_posts)} ·
${esc(t('analytics.campaignReport.measured'))}: ${esc(report.measured_posts)}</p>
<table>${platforms}</table>
<h2>${esc(t('analytics.campaignReport.performance'))}</h2><table>${values}</table>
<h2>${esc(t('analytics.campaignReport.analysis'))}</h2>
<p>${esc(report.analytics_review?.insights || t('analytics.campaignReport.noAnalysis'))}</p>
<ul>${lines}</ul>
<p><small>${esc(t('analytics.campaignReport.metricsNote'))}</small></p>
</body></html>`;
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'report-' + String(project.id).replace(/[^a-zA-Z0-9_-]/g, '') + '.html';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      const reason = error?.response?.data?.detail;
      setReportError(t(reason === 'campaign_report_no_publications'
        ? 'analytics.campaignReport.none'
        : reason === 'campaign_report_publications_pending'
          ? 'analytics.campaignReport.pending' : 'analytics.campaignReport.error'));
    } finally {
      setLoadingReport(false);
    }
  };

  const togglePlatform = (platform) => {
    setActivePlatforms((current) => {
      const isActive = current.includes(platform);
      if (isActive) {
        disabledPlatformIdsRef.current.add(platform);
        return current.filter((p) => p !== platform);
      }
      disabledPlatformIdsRef.current.delete(platform);
      return [...current, platform];
    });
  };

  if (loading) return <p className="text-[var(--text-muted)] text-center py-4 text-xs">{t('analytics.loading')}</p>;
  if (!data) return <p className="text-[var(--text-muted)] text-center py-4 text-xs">--</p>;

  if (compact) {
    return (
      <div>
        <div className="flex justify-between text-xs mb-2">
          <span>{t('analytics.content')}</span>
          <span className="font-bold">{data.total_contents}/{data.target_contents}</span>
        </div>
        <div className="w-full h-2 rounded-full bg-[var(--bg-card)] overflow-hidden mb-3">
          <div className="h-full rounded-full" style={{ width: `${data.completion_pct}%`, background: 'linear-gradient(90deg, var(--gradient-start), var(--gradient-end))' }} />
        </div>
        {Object.entries(data.by_format).map(([k, v]) => (
          <StatBar key={k} label={k} value={v} total={data.total_contents} color={COLORS[k] || '#6b7280'} />
        ))}
        {postMetrics.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--border-color)]">
            <p className="text-[10px] text-[var(--text-muted)] uppercase font-semibold mb-2">{t('analytics.realPerformance')}</p>
            <div className="flex gap-3 text-center">
              <div><p className="text-sm font-bold">{postMetrics.reduce((s, m) => s + (m.normalized?.views || m.metrics?.reach || m.metrics?.impressions || 0), 0).toLocaleString(locale)}</p><p className="text-[9px] text-[var(--text-muted)]">{t('analytics.views')}</p></div>
              <div><p className="text-sm font-bold">{postMetrics.reduce((s, m) => s + (m.normalized?.engagement || m.metrics?.likes || 0), 0).toLocaleString(locale)}</p><p className="text-[9px] text-[var(--text-muted)]">{t('analytics.engagementShort')}</p></div>
              <div><p className="text-sm font-bold">{postMetrics.reduce((s, m) => s + (m.normalized?.sharing || 0), 0).toLocaleString(locale)}</p><p className="text-[9px] text-[var(--text-muted)]">{t('analytics.sharingShort')}</p></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const totals = dashboard?.totals || [];
  const metricGroups = dashboard?.metric_groups || [];
  const platforms = dashboard?.platforms || [];
  const activeMetricGroup = metricGroups.find((m) => m.primary === activeMetric) || metricGroups[0];
  const platformsById = Object.fromEntries(platforms.map((platform) => [platform.id, platform.label]));
  const activeSeries = (dashboard?.series?.[activeMetric] || []).map((row) => ({
    ...row,
    __posts: dashboard?.post_points?.[row.date] || {},
  }));
  const totalViews = totals.reduce((s, m) => s + (m.views || 0), 0);
  const totalEngagement = totals.reduce((s, m) => s + (m.engagement || 0), 0);
  const totalSharing = totals.reduce((s, m) => s + (m.sharing || 0), 0);
  const totalClicks = totals.reduce((s, m) => s + (m.clicks || 0), 0);
  const hasMetrics = postMetrics.length > 0;
  const rangeLabel = t(`analytics.timeRange.options.${rangeDays}`);
  const rangeStart = formatChartDate(dashboard?.range_start, locale, { year: true });
  const rangeEnd = formatChartDate(dashboard?.range_end, locale, { year: true });
  const chartTicks = buildChartTicks(activeSeries.map((row) => row.date), rangeDays);

  return (
    <div className="w-full max-w-none space-y-4 md:space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2"><h3 className="font-semibold" data-testid="analytics-title">{t('analytics.title')}</h3><ContextHelpButton articleId="analytics" label="Guida Analytics" /></div>
          <p className="text-xs text-[var(--text-muted)]">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs flex items-center gap-1" onClick={load}>
            <ArrowClockwise size={13} /> {t('common.refresh')}
          </button>
          <button className="btn-gradient text-xs flex items-center gap-1" onClick={syncMetrics} disabled={syncing}>
            <ArrowClockwise size={12} className={syncing ? 'animate-spin' : ''} />
            {syncing ? t('analytics.syncing') : t('analytics.syncMetrics')}
          </button>
        </div>
      </div>

      {syncSummaryLines?.length > 0 ? (
        <div className="text-[11px] text-[var(--text-muted)] space-y-0.5" data-testid="sync-summary">
          {syncSummaryLines.map((line, index) => <p key={index}>{line}</p>)}
        </div>
      ) : (
        syncMsg && <p className="text-[11px] text-[var(--text-muted)]">{syncMsg}</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5 md:gap-3">
        <MetricChip label={t('analytics.metrics.viewsReach')} value={totalViews} icon={Eye} color="#6366f1" locale={locale} />
        <MetricChip label={t('analytics.metrics.engagement')} value={totalEngagement} icon={TrendUp} color="#f97316" locale={locale} />
        <MetricChip label={t('analytics.metrics.shareSave')} value={totalSharing} icon={ShareNetwork} color="#22c55e" locale={locale} />
        <MetricChip label={t('analytics.metrics.clicks')} value={totalClicks} icon={CursorClick} color="#38bdf8" locale={locale} />
        <MetricChip label={t('analytics.metrics.measuredPosts')} value={postMetrics.length} icon={ChartBar} color="#a855f7" locale={locale} />
      </div>

      <div className="card p-4 md:p-5">
        <div className="flex flex-col gap-3 md:gap-4 mb-4 md:mb-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <TrendUp size={16} weight="fill" color="var(--gradient-start)" /> {t('analytics.chartTitle')}
              </h4>
              <p className="hidden sm:block text-xs text-[var(--text-muted)] mt-1">{activeMetricGroup ? t(`analytics.metricGroups.${activeMetricGroup.id}.description`) : t('analytics.chartFallbackDescription')}</p>
            </div>
            {dashboard?.last_sync_at && (
              <span className="text-[10px] text-[var(--text-muted)]">{t('analytics.lastSync', { date: new Date(dashboard.last_sync_at).toLocaleString(locale) })}</span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <CalendarBlank size={15} weight="fill" color="var(--gradient-start)" />
              <span className="font-semibold">{t('analytics.timeRange.label')}</span>
              <select
                value={rangeDays}
                onChange={(event) => {
                  setAiInsights(null);
                  setRangeDays(Number(event.target.value));
                }}
                className="rounded-xl px-3 py-2 text-xs font-semibold text-white outline-none"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                aria-label={t('analytics.timeRange.label')}
              >
                {TIME_RANGES.map((days) => (
                  <option key={days} value={days}>{t(`analytics.timeRange.options.${days}`)}</option>
                ))}
              </select>
            </label>
            <span className="text-[10px] text-[var(--text-muted)]">
              {t('analytics.timeRange.summary', { range: rangeLabel, start: rangeStart, end: rangeEnd })}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {metricGroups.map((group) => {
              const Icon = METRIC_ICONS[group.primary] || ChartBar;
              const selected = activeMetric === group.primary;
              return (
                <button
                  key={group.id}
                  onClick={() => setActiveMetric(group.primary)}
                  className={`px-2.5 md:px-3 py-1.5 md:py-2 rounded-xl text-[11px] md:text-xs font-semibold flex items-center gap-1.5 md:gap-2 transition ${selected ? 'text-white' : 'text-[var(--text-muted)]'}`}
                  style={{
                    background: selected ? 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))' : 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <Icon size={14} weight="fill" /> {t(`analytics.metricGroups.${group.id}.label`)}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            {platforms.map((platform) => {
              const Icon = PLATFORM_ICONS[platform.id] || ChartBar;
              const selected = activePlatforms.includes(platform.id);
              return (
                <button
                  key={platform.id}
                  data-testid={`platform-toggle-${platform.id}`}
                  data-active={selected}
                  onClick={() => togglePlatform(platform.id)}
                  className="px-2.5 md:px-3 py-1.5 rounded-full text-[11px] md:text-xs font-semibold flex items-center gap-1.5 transition"
                  style={{
                    opacity: selected ? 1 : 0.45,
                    color: selected ? '#fff' : 'var(--text-muted)',
                    background: selected ? 'rgba(255,255,255,0.08)' : 'var(--bg-secondary)',
                    border: `1px solid ${selected ? COLORS[platform.id] || 'var(--border-color)' : 'var(--border-color)'}`,
                  }}
                >
                  <Icon size={13} weight="fill" color={COLORS[platform.id]} /> {platform.label}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[var(--text-muted)]">
            {t('analytics.postNodesHint')}
          </p>
        </div>

        {activeSeries.length > 0 ? (
          <div className="h-[260px] md:h-[360px] xl:h-[440px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={activeSeries} margin={{ top: 10, right: 24, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  tickFormatter={(value) => formatChartDate(value, locale)}
                  ticks={chartTicks}
                  interval={0}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} width={45} />
                <Tooltip content={<ChartTooltip locale={locale} metricKey={activeMetric} platformsById={platformsById} t={t} />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {platforms.filter((p) => activePlatforms.includes(p.id)).map((platform) => (
                  <Line
                    key={platform.id}
                    type="monotone"
                    dataKey={platform.id}
                    name={platform.label}
                    stroke={COLORS[platform.id] || '#94a3b8'}
                    strokeWidth={2.5}
                    dot={(props) => <PostAwareDot {...props} platformId={platform.id} rangeDays={rangeDays} />}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12">
            <TrendUp size={34} className="mx-auto mb-3 text-[var(--text-muted)]" />
            <p className="text-sm text-[var(--text-muted)] mb-1">{t('analytics.noCurveTitle')}</p>
            <p className="text-xs text-[var(--text-muted)]">{t('analytics.noCurveHint', { syncLabel: t('analytics.syncMetrics') })}</p>
          </div>
        )}
      </div>

      {Object.entries(dashboard?.availability || {}).some(([, item]) => item.status !== 'available') && (
        <div className="card">
          <h4 className="text-sm font-semibold mb-3">{t('analytics.coverageTitle')}</h4>
          <div className="space-y-2">
            {Object.entries(dashboard.availability).filter(([, item]) => item.status !== 'available').map(([platform, item]) => {
              const Icon = PLATFORM_ICONS[platform] || ChartBar;
              return (
                <div key={platform} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                  <Icon size={16} weight="fill" color={COLORS[platform]} className="mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold capitalize">{platform}</p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {platformAvailabilityMessage(item, platform, t)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3">
        {totals.map((platform) => {
          const Icon = PLATFORM_ICONS[platform.platform] || ChartBar;
          return (
            <motion.div key={platform.platform} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="stat-card">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} weight="fill" color={COLORS[platform.platform]} />
                  <p className="text-sm font-semibold">{platform.label}</p>
                </div>
                <p className="text-[10px] text-[var(--text-muted)]">{t('analytics.measuredPostsRatio', { measured: platform.measured_posts, total: platform.posts })}</p>
                <p className="text-lg font-bold mt-2">{formatNumber(platform.engagement, locale)}</p>
                <p className="text-[10px] text-[var(--text-muted)]">{t('analytics.engagementRate', { rate: platform.engagement_rate })}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 md:gap-3">
        {[
          { label: t('analytics.content'), value: `${data.total_contents}/${data.target_contents}`, icon: Article, color: 'var(--gradient-start)' },
          { label: t('analytics.completion'), value: `${data.completion_pct}%`, icon: Target, color: '#22c55e' },
          { label: t('analytics.media'), value: data.media_count, icon: Image, color: '#f97316' },
          { label: t('analytics.published'), value: data.queue_by_status?.published || 0, icon: Queue, color: '#a855f7' },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="stat-card">
            <div>
              <p className="text-[var(--text-secondary)] text-xs mb-1">{s.label}</p>
              <p className="text-xl font-bold">{s.value}</p>
            </div>
            <s.icon size={24} weight="fill" style={{ color: s.color }} />
          </motion.div>
        ))}
      </div>

      <div className="card p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-semibold text-sm">{t('analytics.campaignReport.title')}</h4>
            <p className="text-xs text-[var(--text-muted)]">{t('analytics.campaignReport.description')}</p>
          </div>
          <button className="btn-ghost text-xs" onClick={downloadCampaignReport} disabled={loadingReport || !campaignReportReady}>
            {loadingReport ? t('analytics.campaignReport.generating') : t('analytics.campaignReport.download')}
          </button>
        </div>
        {!campaignReportReady && <p className="text-xs text-[var(--text-muted)] mt-2">{t(campaignQueuedCount ? 'analytics.campaignReport.pending' : 'analytics.campaignReport.none')}</p>}
        {reportError && <p role="alert" className="text-xs text-red-400 mt-2">{reportError}</p>}
      </div>

      <div className="card p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold flex items-center gap-2"><Sparkle size={16} weight="fill" color="#a855f7" /> {t('analytics.aiInsights')}</h4>
          <button className="btn-ghost text-xs flex items-center gap-1" onClick={loadAiInsights} disabled={loadingAi}>
            <Lightning size={12} weight="fill" /> {loadingAi ? t('analytics.analyzing') : t('analytics.analyze')}
          </button>
        </div>

        {aiInsights ? (
          <div className="space-y-4">
            {aiInsights.insights && (
              <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.08) 100%)', border: '1px solid rgba(99,102,241,0.2)' }}>
                <p className="text-sm leading-relaxed">{aiInsights.insights}</p>
              </div>
            )}
            {aiInsights.recommendations?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('analytics.recommendations')}</p>
                <div className="space-y-2">
                  {aiInsights.recommendations.map((r, i) => (
                    <div key={i} className="flex gap-2 items-start p-2.5 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                      <Star size={14} weight="fill" color="#f97316" className="flex-shrink-0 mt-0.5" />
                      <p className="text-xs">{r}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-muted)] text-center py-4">
            {hasMetrics ? t('analytics.aiPromptWithMetrics', { label: t('analytics.analyze') }) : t('analytics.aiPromptNoMetrics')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="card p-4 md:p-5">
          <h4 className="text-sm font-semibold mb-4">{t('analytics.byFormat')}</h4>
          {Object.entries(data.by_format).map(([k, v]) => (
            <StatBar key={k} label={k} value={v} total={data.total_contents} color={COLORS[k] || '#6b7280'} />
          ))}
        </div>
        <div className="card p-4 md:p-5">
          <h4 className="text-sm font-semibold mb-4">{t('analytics.byPillar')}</h4>
          {Object.entries(data.by_pillar).map(([k, v]) => (
            <StatBar key={k} label={k} value={v} total={data.total_contents} color={COLORS[k] || '#6b7280'} />
          ))}
        </div>
      </div>
    </div>
  );
}
